import { useState } from 'react';
import { User, MapPin, Smartphone } from 'lucide-react';
import ModalModel from './ModalModel';
import onboardingService, { onboardingSteps } from '../../services/onboardingService';

const OnboardingModal = ({ step, user, onComplete, onDismiss, onSkip }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  if (!step) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation basique — « au moins un » pour une étape `requireAny`
    // (Mobile Money : Airtel ou Moov suffit), « tous » sinon.
    const newErrors = {};
    if (step.requireAny) {
      const anyFilled = step.fields.some((field) => formData[field]?.trim());
      if (!anyFilled) {
        step.fields.forEach((field) => {
          newErrors[field] = 'Renseigne au moins un numéro';
        });
      }
    } else {
      step.fields.forEach((field) => {
        if (!formData[field]?.trim()) {
          newErrors[field] = 'Ce champ est requis';
        }
      });
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      await onComplete(formData);
      onboardingService.markCompleted(step.id);
    } catch (error) {
      console.error('Erreur onboarding:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    onboardingService.dismissStep(step.id);
    onDismiss?.();
  };

  const handleSkip = () => {
    onboardingService.skipStep(step.id);
    onSkip?.();
  };

  // Obtenir l'icône selon le type d'étape
  const getIcon = () => {
    switch (step.id) {
      case onboardingSteps.PROFILE_NAME.id:
        return <User className="w-5 h-5 text-orange-500" />;
      case onboardingSteps.ADDRESS.id:
        return <MapPin className="w-5 h-5 text-green-600" />;
      case onboardingSteps.MOBILE_MONEY.id:
        return <Smartphone className="w-5 h-5 text-orange-600" />;
      default:
        return null;
    }
  };

  // Rendu des champs selon l'étape
  const renderFields = () => {
    switch (step.id) {
      case onboardingSteps.PROFILE_NAME.id:
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Prénom
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name || ''}
                onChange={handleChange}
                placeholder="Ton prénom"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.first_name
                    ? 'border-red-500 focus:ring-red-400'
                    : 'focus:ring-orange-300'
                }`}
              />
              {errors.first_name && (
                <p className="text-red-600 text-sm mt-1">{errors.first_name}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom
              </label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name || user?.lastName || ''}
                onChange={handleChange}
                placeholder="Ton nom"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.last_name
                    ? 'border-red-500 focus:ring-red-400'
                    : 'focus:ring-orange-300'
                }`}
              />
              {errors.last_name && (
                <p className="text-red-600 text-sm mt-1">{errors.last_name}</p>
              )}
            </div>
          </>
        );

      case onboardingSteps.ADDRESS.id:
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse de livraison
            </label>
            <textarea
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
              placeholder="Ex: Quartier, Rue, Repère..."
              rows={3}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.address
                  ? 'border-red-500 focus:ring-red-400'
                  : 'focus:ring-orange-300'
              }`}
            />
            {errors.address && (
              <p className="text-red-600 text-sm mt-1">{errors.address}</p>
            )}
          </div>
        );

      case onboardingSteps.MOBILE_MONEY.id:
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro Airtel Money
              </label>
              <input
                type="tel"
                name="mobile_money_phone"
                value={formData.mobile_money_phone || ''}
                onChange={handleChange}
                placeholder="Ex: 07XXXXXXXX"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.mobile_money_phone
                    ? 'border-red-500 focus:ring-red-400'
                    : 'focus:ring-orange-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Numéro Moov Money
              </label>
              <input
                type="tel"
                name="moov_money_phone"
                value={formData.moov_money_phone || ''}
                onChange={handleChange}
                placeholder="Ex: 06XXXXXXXX"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.moov_money_phone
                    ? 'border-red-500 focus:ring-red-400'
                    : 'focus:ring-orange-300'
                }`}
              />
            </div>
            {(errors.mobile_money_phone || errors.moov_money_phone) && (
              <p className="text-red-600 text-sm">
                {errors.mobile_money_phone || errors.moov_money_phone}
              </p>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <ModalModel
      title={step.title}
      icon={getIcon()}
      active={true}
      onClose={handleDismiss}
    >
      <div className="space-y-4">
        <p className="text-gray-600 text-center">{step.description}</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {renderFields()}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSkip}
              className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Plus tard
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-3 px-4 rounded-lg text-white transition-colors ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-main-button hover:bg-main-button-hover'
              }`}
            >
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </ModalModel>
  );
};

export default OnboardingModal;
